import React, { useState, useEffect, useRef } from 'react';

const TimeScrollPicker = ({ 
  initialHour = '09', 
  initialMinute = '00', 
  initialAmPm = 'AM',
  onTimeChange
}) => {
  const [hour, setHour] = useState(initialHour);
  const [minute, setMinute] = useState(initialMinute);
  const [amPm, setAmPm] = useState(initialAmPm);
  
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const amPmRef = useRef(null);
  
  const hours = Array.from({ length: 12 }, (_, i) => {
    const num = i + 1;
    return num.toString().padStart(2, '0');
  });
  
  const minutes = Array.from({ length: 60 }, (_, i) => {
    return i.toString().padStart(2, '0');
  });
  
  const amPmOptions = ['AM', 'PM'];

  useEffect(() => {
    // Set initial position with different behavior for iOS vs other platforms
    const setupInitialPosition = () => {
      // Get current iOS status inside the function to avoid initialization issues
      const isIOSDevice = detectIOS();
      
      if (hourRef.current) {
        const index = hours.indexOf(initialHour);
        if (index !== -1) {
          const scrollPosition = index * 40; // Each element is 40px tall
          
          hourRef.current.scrollTo({
            top: scrollPosition,
            // Use different behavior based on platform
            behavior: isIOSDevice ? 'auto' : 'smooth'
          });
        }
      }
      
      if (minuteRef.current) {
        const index = minutes.indexOf(initialMinute);
        if (index !== -1) {
          const scrollPosition = index * 40;
          
          minuteRef.current.scrollTo({
            top: scrollPosition,
            behavior: isIOSDevice ? 'auto' : 'smooth'
          });
        }
      }
      
      if (amPmRef.current) {
        const index = amPmOptions.indexOf(initialAmPm);
        if (index !== -1) {
          const scrollPosition = index * 40;
          
          amPmRef.current.scrollTo({
            top: scrollPosition,
            behavior: isIOSDevice ? 'auto' : 'smooth'
          });
        }
      }
    };
    
    // First set the position immediately
    setupInitialPosition();
    
    // Then use a short delay for smoother rendering
    setTimeout(setupInitialPosition, 50);
    
    // Add longer delay for iOS to ensure proper rendering after modal animation
    if (detectIOS()) {
      setTimeout(setupInitialPosition, 300);
    }
  }, [initialHour, initialMinute, initialAmPm, hours, minutes, amPmOptions]);

  useEffect(() => {
    // Notify parent component of time changes
    if (onTimeChange) {
      onTimeChange({ hour, minute, amPm });
    }
  }, [hour, minute, amPm, onTimeChange]);

  // Add timeout for smoother iOS scrolling
  const scrollTimeoutRef = useRef(null);
  
  // Check for iOS platform - moved to a separate function to avoid initialization errors
  const detectIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };
  
  const isIOS = detectIOS();
  
  // Longer delay for iOS to allow more momentum scrolling
  const scrollDelay = isIOS ? 350 : 50;
  
  const handleHourScroll = (e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // For iOS, don't interfere with the momentum scrolling while user is actively scrolling
    // Only process the scroll event after scrolling has naturally stopped
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollPosition = e.target.scrollTop;
      const index = Math.round(scrollPosition / 40);
      if (index >= 0 && index < hours.length) {
        setHour(hours[index]);
        
        // Snap to the exact position for the selected hour only after momentum has finished
        if (hourRef.current) {
          const targetPosition = index * 40;
          
          // Use a subtle snap with very minimal animation for iOS
          if (detectIOS()) {
            hourRef.current.scrollTo({
              top: targetPosition,
              behavior: 'auto' // Use auto for iOS for more natural feel
            });
          } else {
            hourRef.current.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    }, scrollDelay); // Longer delay for iOS to allow more momentum
  };

  const handleMinuteScroll = (e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollPosition = e.target.scrollTop;
      const index = Math.round(scrollPosition / 40);
      if (index >= 0 && index < minutes.length) {
        setMinute(minutes[index]);
        
        // Snap to the exact position after momentum has finished
        if (minuteRef.current) {
          const targetPosition = index * 40;
          
          // Different behavior for iOS vs other platforms
          if (detectIOS()) {
            minuteRef.current.scrollTo({
              top: targetPosition,
              behavior: 'auto' // Use auto for iOS for more natural feel
            });
          } else {
            minuteRef.current.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    }, scrollDelay);
  };

  const handleAmPmScroll = (e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollPosition = e.target.scrollTop;
      const index = Math.round(scrollPosition / 40);
      if (index >= 0 && index < amPmOptions.length) {
        setAmPm(amPmOptions[index]);
        
        // Snap to the exact position after momentum has finished
        if (amPmRef.current) {
          const targetPosition = index * 40;
          
          // Different behavior for iOS vs other platforms
          if (detectIOS()) {
            amPmRef.current.scrollTo({
              top: targetPosition,
              behavior: 'auto' // Use auto for iOS for more natural feel
            });
          } else {
            amPmRef.current.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    }, scrollDelay);
  };

  return (
    <div className="time-scroll-picker">
      <style>
        {`
          .time-scroll-picker {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0;
            position: relative;
            width: 220px;
            height: 160px;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            background-color: white;
            margin: 10px auto;
          }
          
          .scroll-column {
            flex: 1;
            height: 100%;
            overflow-y: scroll;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: y mandatory;
            border-right: 1px solid #f0f0f0;
            scrollbar-width: none;
            -ms-overflow-style: none;
            position: relative;
            padding: 0 2px;
            -webkit-momentum-scrolling: touch;
            scroll-behavior: smooth;
            scroll-snap-stop: always;
            -webkit-tap-highlight-color: transparent;
            overscroll-behavior-y: contain; /* Prevents page scroll when reaching the end */
          }
          
          .scroll-column:last-child {
            border-right: none;
          }
          
          .scroll-column::-webkit-scrollbar {
            display: none;
          }
          
          .scroll-item {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 40px;
            scroll-snap-align: center;
            font-size: 16px;
            font-weight: 500;
            color: #666;
          }
          
          .scroll-item.period {
            font-weight: 600;
            color: #2A9D8F;
          }
          
          .scroll-column::before,
          .scroll-column::after {
            content: '';
            display: block;
            height: 60px;
          }
          
          /* Add iOS specific momentum scrolling for better experience */
          @supports (-webkit-touch-callout: none) {
            .scroll-column {
              -webkit-overflow-scrolling: touch;
              scroll-behavior: auto; /* Let iOS handle native momentum */
              -webkit-scroll-snap-type: none; /* Disable snap during momentum */
              scroll-snap-type: none;
              scroll-padding: 0;
            }
            
            /* Enhance the momentum effect */
            .time-scroll-picker {
              -webkit-touch-callout: none;
              -webkit-user-select: none;
              touch-action: pan-y;
            }
            
            /* Increase the "friction" to make scrolling last longer */
            .scroll-column::-webkit-scrollbar {
              width: 0px;
              background: transparent;
            }
          }
          
          .selection-indicator {
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            height: 40px;
            border-top: 1px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
            pointer-events: none;
            transform: translateY(-50%);
            z-index: 1;
            background-color: rgba(42, 157, 143, 0.08);
          }
          
          .column-labels {
            display: flex;
            width: 100%;
            justify-content: space-between;
            padding: 0 5px;
            margin-bottom: 5px;
          }
          
          .column-label {
            flex: 1;
            text-align: center;
            font-size: 12px;
            font-weight: 500;
            color: #666;
            padding: 0 2px;
          }
          
        `}
      </style>
      
      <div className="selection-indicator"></div>
      
      <div
        className="scroll-column"
        ref={hourRef}
        onScroll={handleHourScroll}
      >
        {hours.map((h) => (
          <div key={h} className="scroll-item">
            {h}
          </div>
        ))}
      </div>
      
      <div
        className="scroll-column"
        ref={minuteRef}
        onScroll={handleMinuteScroll}
      >
        {minutes.map((m) => (
          <div key={m} className="scroll-item">
            {m}
          </div>
        ))}
      </div>
      
      <div
        className="scroll-column"
        ref={amPmRef}
        onScroll={handleAmPmScroll}
      >
        {amPmOptions.map((option) => (
          <div key={option} className="scroll-item period">
            {option}
          </div>
        ))}
        {/* No column label for AM/PM */}
      </div>
    </div>
  );
};

export default TimeScrollPicker;